import React from "react";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import { useCountUp, formatNumber } from "../../lib/hooks";
import { Reveal } from "../primitives/Reveal";
import { AccentTitle } from "../primitives/AccentTitle";

const STATS = [
    { key: "domains", target: 240, suffix: "K", plus: true, accent: "#8A1E12" },
    { key: "records", target: 1.8, suffix: "B", decimals: 1, accent: "#0D525C" },
    { key: "uptime", target: 99.99, suffix: "%", decimals: 2, accent: "#E59500" },
    { key: "support", target: 24, suffix: "/7", accent: "#1A1C1D" },
];

const StatCell = ({ stat }) => {
    const { ref, value, done } = useCountUp(stat.target, { duration: 1600, decimals: stat.decimals || 0 });
    const { t } = useTranslation();
    const display = stat.decimals != null ? value.toFixed(stat.decimals) : formatNumber(value);
    return (
        <div
            ref={ref}
            className="bento-card p-8 lg:p-10"
            data-testid={`stat-${stat.key}`}
        >
            <p
                className="flex items-baseline font-display text-[64px] font-extrabold leading-none tracking-[-0.03em] tabular-nums num-ltr lg:text-[88px]"
                style={{ color: stat.accent }}
            >
                <span>{display}</span>
                <span className="text-[0.55em]">{stat.suffix}</span>
                {stat.plus && (
                    <motion.span
                        initial={{ opacity: 0, scale: 0.6 }}
                        animate={done ? { opacity: 1, scale: 1 } : {}}
                        transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
                        className="ms-1 inline-block align-top text-[0.45em]"
                    >+</motion.span>
                )}
            </p>
            <p className="mt-5 text-[17px] font-semibold text-ink-800">{t(`home.stats.${stat.key}`)}</p>
        </div>
    );
};

export const Stats = () => {
    const { t } = useTranslation();
    return (
        <section className="bg-mudbrick-200 py-24 lg:py-32" data-testid="stats-band">
            <div className="mx-auto max-w-[1320px] px-5 lg:px-8">
                <Reveal className="mb-12 max-w-3xl">
                    <p className="eyebrow">{t("home.stats.kicker")}</p>
                    <AccentTitle
                        component="h2"
                        className="section-headline mt-5"
                        title={t("home.stats.title")}
                        accent={t("home.stats.titleAccent")}
                        accentColor="#8A1E12"
                    />
                </Reveal>
                <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
                    {STATS.map((s) => <StatCell key={s.key} stat={s} />)}
                </div>
                <p className="caption mt-8 font-semibold text-ink-700">{t("home.stats.footnote")}</p>
            </div>
        </section>
    );
};
